import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { FloppyDisk, Globe, UploadSimple, Image as ImageIcon, Shield } from '@phosphor-icons/react';
import { API, BACKEND_URL } from './adminConstants';

const DEFAULT_STATS = [
  { value: '6', label: 'Core Services' },
  { value: '100+', label: 'Business Clients' },
  { value: '24/7', label: 'Support Available' },
  { value: '100%', label: 'Local Team' },
];

export function WebsiteCMS() {
  const { getAuthHeader } = useAuth();
  const [hero, setHero] = useState(null);
  const [about, setAbout] = useState(null);
  const [contactHero, setContactHero] = useState(null);
  const [contactDetails, setContactDetails] = useState(null);
  const [geoblock, setGeoblock] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/page-content/hero`),
      axios.get(`${API}/page-content/about`),
      axios.get(`${API}/page-content/contact_hero`),
      axios.get(`${API}/page-content/contact_details`),
      axios.get(`${API}/admin/geoblock`, { headers: getAuthHeader() }),
    ]).then(([hRes, aRes, cRes, dRes, gRes]) => {
      const h = hRes.data || {};
      setHero({
        overline: h.overline || '',
        heading: h.heading || '',
        heading_highlight: h.heading_highlight || '',
        heading_suffix: h.heading_suffix || '',
        description: h.description || '',
        cta_primary: h.cta_primary || 'Get a Free Quote',
        cta_secondary: h.cta_secondary || 'Our Services',
        background_image: h.background_image || '',
        stats: Array.isArray(h.stats) && h.stats.length === 4 ? h.stats : DEFAULT_STATS,
      });
      setAbout(aRes.data);
      const c = cRes.data || {};
      setContactHero({
        overline: c.overline || '',
        heading: c.heading || '',
        heading_highlight: c.heading_highlight || '',
        heading_suffix: c.heading_suffix || '',
        description: c.description || '',
      });
      const d = dRes.data || {};
      setContactDetails({
        phone_display: d.phone_display || '',
        phone_href: d.phone_href || '',
        email: d.email || '',
        address_line_1: d.address_line_1 || '',
        address_line_2: d.address_line_2 || '',
        address_line_3: d.address_line_3 || '',
        linkedin_url: d.linkedin_url || '',
        instagram_url: d.instagram_url || '',
        trustpilot_url: d.trustpilot_url || '',
      });
      const g = gRes.data || {};
      setGeoblock({
        blocked_countries_text: Array.isArray(g.blocked_countries) ? g.blocked_countries.join(', ') : '',
        block_vpn: !!g.block_vpn,
        updated_at: g.updated_at || null,
        updated_by: g.updated_by || null,
      });
    }).catch(console.error);
  }, []);

  const savePage = async (page, content) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/page-content/${page}`, content, { headers: getAuthHeader() });
      toast.success(`${page} content saved`);
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const formData = new FormData(); formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/services/upload-image`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      setHero(prev => ({ ...prev, background_image: `${BACKEND_URL}${res.data.image_url}` }));
      toast.success('Background image uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
    e.target.value = '';
  };

  const updateStat = (i, key, val) => {
    const next = [...hero.stats];
    next[i] = { ...next[i], [key]: val };
    setHero({ ...hero, stats: next });
  };

  if (!hero || !about || !contactHero || !contactDetails || !geoblock) return <p className="text-slate-500">Loading...</p>;

  return (
    <div data-testid="website-cms">
      <h1 className="font-outfit font-bold text-2xl text-slate-900 mb-6">Website Content Manager</h1>
      <div className="space-y-6">

        {/* Hero Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><Globe size={20} />Home Page Hero</h2>

          {/* Background image */}
          <div className="mb-5">
            <Label className="text-xs flex items-center gap-1.5 mb-2"><ImageIcon size={14} className="text-lime-600" weight="duotone" />Background photo</Label>
            <div className="flex gap-3 items-start">
              <div className="flex-1">
                <Input
                  value={hero.background_image}
                  onChange={e => setHero({ ...hero, background_image: e.target.value })}
                  placeholder="Paste image URL or upload..."
                  data-testid="hero-bg-url"
                />
              </div>
              <label className="cursor-pointer shrink-0">
                <Button variant="outline" size="sm" asChild disabled={uploading}>
                  <span><UploadSimple size={15} className="mr-1" />{uploading ? 'Uploading…' : 'Upload'}</span>
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleHeroImageUpload}
                  data-testid="hero-bg-upload"
                />
              </label>
            </div>
            {hero.background_image && (
              <div className="mt-2 w-64 h-32 rounded-lg overflow-hidden border border-slate-200 bg-neutral-100">
                <img src={hero.background_image} alt="Hero background preview" className="w-full h-full object-cover" />
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-1.5">Recommended: 1920×1080 landscape. A dark overlay is automatically applied for legibility.</p>
          </div>

          {/* Headings */}
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs">Overline</Label><Input value={hero.overline} onChange={e => setHero({...hero, overline: e.target.value})} /></div>
            <div><Label className="text-xs">Heading</Label><Input value={hero.heading} onChange={e => setHero({...hero, heading: e.target.value})} /></div>
            <div><Label className="text-xs">Heading Highlight (green)</Label><Input value={hero.heading_highlight} onChange={e => setHero({...hero, heading_highlight: e.target.value})} /></div>
            <div><Label className="text-xs">Heading Suffix</Label><Input value={hero.heading_suffix} onChange={e => setHero({...hero, heading_suffix: e.target.value})} /></div>
          </div>
          <div className="mt-3"><Label className="text-xs">Description</Label><Textarea value={hero.description} onChange={e => setHero({...hero, description: e.target.value})} rows={3} /></div>

          <div className="grid grid-cols-2 gap-4 mt-3">
            <div><Label className="text-xs">Primary CTA label</Label><Input value={hero.cta_primary} onChange={e => setHero({...hero, cta_primary: e.target.value})} placeholder="e.g. Get a Free Quote" /></div>
            <div><Label className="text-xs">Secondary CTA label</Label><Input value={hero.cta_secondary} onChange={e => setHero({...hero, cta_secondary: e.target.value})} placeholder="e.g. Our Services" /></div>
          </div>

          {/* Stats */}
          <div className="mt-5 pt-5 border-t border-slate-100">
            <Label className="text-sm font-semibold text-slate-800 mb-3 block">Banner statistics</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {hero.stats.map((s, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Stat {i + 1}</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-[11px]">Value</Label>
                      <Input
                        value={s.value}
                        onChange={e => updateStat(i, 'value', e.target.value)}
                        placeholder="e.g. 100+"
                        data-testid={`hero-stat-value-${i}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[11px]">Label</Label>
                      <Input
                        value={s.label}
                        onChange={e => updateStat(i, 'label', e.target.value)}
                        placeholder="e.g. Business Clients"
                        data-testid={`hero-stat-label-${i}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button onClick={() => savePage('hero', hero)} disabled={saving} className="mt-5 bg-lime-600 hover:bg-lime-700" data-testid="save-hero-btn">
            <FloppyDisk size={15} className="mr-1" />Save Hero
          </Button>
        </div>

        {/* About */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><Globe size={20} />About Page</h2>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs">Header Overline</Label><Input value={about.header_overline || ''} onChange={e => setAbout({...about, header_overline: e.target.value})} /></div>
            <div><Label className="text-xs">Header Heading</Label><Input value={about.header_heading || ''} onChange={e => setAbout({...about, header_heading: e.target.value})} /></div>
          </div>
          <div className="mt-3"><Label className="text-xs">Header Description</Label><Textarea value={about.header_description || ''} onChange={e => setAbout({...about, header_description: e.target.value})} rows={3} /></div>
          <div className="mt-3"><Label className="text-xs">Mission Text</Label><Textarea value={about.mission_text || ''} onChange={e => setAbout({...about, mission_text: e.target.value})} rows={3} /></div>
          <div className="mt-3"><Label className="text-xs">Vision Text</Label><Textarea value={about.vision_text || ''} onChange={e => setAbout({...about, vision_text: e.target.value})} rows={3} /></div>
          <Button onClick={() => savePage('about', about)} disabled={saving} className="mt-3 bg-lime-600 hover:bg-lime-700"><FloppyDisk size={15} className="mr-1" />Save About</Button>
        </div>

        {/* Contact Page Hero */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="cms-contact-hero">
          <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2"><Globe size={20} />Contact Page — Hero</h2>
          <p className="text-slate-500 text-xs mb-4">Top section of the public Contact page. The Highlight word renders in green next to the Heading (e.g. &quot;Contact <span className="text-lime-600">Us</span>&quot;).</p>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs">Overline</Label><Input data-testid="contact-hero-overline-input" value={contactHero.overline} onChange={e => setContactHero({ ...contactHero, overline: e.target.value })} placeholder="Get In Touch" /></div>
            <div><Label className="text-xs">Heading</Label><Input data-testid="contact-hero-heading-input" value={contactHero.heading} onChange={e => setContactHero({ ...contactHero, heading: e.target.value })} placeholder="Contact" /></div>
            <div><Label className="text-xs">Heading Highlight (green)</Label><Input data-testid="contact-hero-highlight-input" value={contactHero.heading_highlight} onChange={e => setContactHero({ ...contactHero, heading_highlight: e.target.value })} placeholder="Us" /></div>
            <div><Label className="text-xs">Heading Suffix</Label><Input data-testid="contact-hero-suffix-input" value={contactHero.heading_suffix} onChange={e => setContactHero({ ...contactHero, heading_suffix: e.target.value })} placeholder="(optional)" /></div>
          </div>
          <div className="mt-3"><Label className="text-xs">Description</Label><Textarea data-testid="contact-hero-description-input" value={contactHero.description} onChange={e => setContactHero({ ...contactHero, description: e.target.value })} rows={3} placeholder="Ready to discuss your waste management needs?..." /></div>
          <Button onClick={() => savePage('contact_hero', contactHero)} disabled={saving} className="mt-3 bg-lime-600 hover:bg-lime-700" data-testid="save-contact-hero-btn"><FloppyDisk size={15} className="mr-1" />Save Contact Hero</Button>
        </div>

        {/* Contact Page Details */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="cms-contact-details">
          <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2"><Globe size={20} />Contact Page — Details</h2>
          <p className="text-slate-500 text-xs mb-4">Phone, email, address and social media links displayed in the Contact section of the public site. The Phone Link should start with <code>tel:</code> and contain only digits (e.g. <code>tel:07821666083</code>).</p>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-xs">Phone (display)</Label><Input data-testid="contact-phone-display-input" value={contactDetails.phone_display} onChange={e => setContactDetails({ ...contactDetails, phone_display: e.target.value })} placeholder="07821 666 083" /></div>
            <div><Label className="text-xs">Phone Link (tel:)</Label><Input data-testid="contact-phone-href-input" value={contactDetails.phone_href} onChange={e => setContactDetails({ ...contactDetails, phone_href: e.target.value })} placeholder="tel:07821666083" /></div>
            <div className="col-span-2"><Label className="text-xs">Email</Label><Input data-testid="contact-email-input" value={contactDetails.email} onChange={e => setContactDetails({ ...contactDetails, email: e.target.value })} placeholder="enquiries@nsecltd.co.uk" /></div>
            <div className="col-span-2"><Label className="text-xs">Address Line 1</Label><Input data-testid="contact-address1-input" value={contactDetails.address_line_1} onChange={e => setContactDetails({ ...contactDetails, address_line_1: e.target.value })} placeholder="Unit 5-6 Westlands Distribution Park" /></div>
            <div><Label className="text-xs">Address Line 2</Label><Input data-testid="contact-address2-input" value={contactDetails.address_line_2} onChange={e => setContactDetails({ ...contactDetails, address_line_2: e.target.value })} placeholder="Winterstoke Road" /></div>
            <div><Label className="text-xs">Address Line 3</Label><Input data-testid="contact-address3-input" value={contactDetails.address_line_3} onChange={e => setContactDetails({ ...contactDetails, address_line_3: e.target.value })} placeholder="Weston-Super-Mare, BS24 9AD" /></div>
            <div className="col-span-2"><Label className="text-xs">LinkedIn URL</Label><Input data-testid="contact-linkedin-input" value={contactDetails.linkedin_url} onChange={e => setContactDetails({ ...contactDetails, linkedin_url: e.target.value })} placeholder="https://www.linkedin.com/company/..." /></div>
            <div><Label className="text-xs">Instagram URL</Label><Input data-testid="contact-instagram-input" value={contactDetails.instagram_url} onChange={e => setContactDetails({ ...contactDetails, instagram_url: e.target.value })} placeholder="https://www.instagram.com/..." /></div>
            <div><Label className="text-xs">Trustpilot URL</Label><Input data-testid="contact-trustpilot-input" value={contactDetails.trustpilot_url} onChange={e => setContactDetails({ ...contactDetails, trustpilot_url: e.target.value })} placeholder="https://uk.trustpilot.com/review/..." /></div>
          </div>
          <Button onClick={() => savePage('contact_details', contactDetails)} disabled={saving} className="mt-4 bg-lime-600 hover:bg-lime-700" data-testid="save-contact-details-btn"><FloppyDisk size={15} className="mr-1" />Save Contact Details</Button>
        </div>

        {/* Geo-Block */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="cms-geoblock">
          <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2"><Shield size={20} />Country &amp; VPN Block List</h2>
          <p className="text-slate-500 text-xs mb-1">Visitors from any of the countries listed here are refused access to the entire website (HTTP 451). This affects every page and every API endpoint.</p>
          <p className="text-slate-500 text-xs mb-4">Use ISO-3166 alpha-2 codes separated by commas. Examples: <code>SG</code> (Singapore), <code>RU</code> (Russia), <code>CN</code> (China), <code>IR</code> (Iran). Case doesn&apos;t matter. Leave empty to block nobody.</p>
          <Label className="text-xs">Blocked country codes</Label>
          <Input
            data-testid="geoblock-codes-input"
            value={geoblock.blocked_countries_text}
            onChange={e => setGeoblock({ ...geoblock, blocked_countries_text: e.target.value })}
            placeholder="SG, RU"
            className="font-mono"
          />

          <div className="mt-4 flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
            <input
              type="checkbox"
              id="block-vpn-toggle"
              data-testid="block-vpn-toggle"
              checked={geoblock.block_vpn}
              onChange={e => setGeoblock({ ...geoblock, block_vpn: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-lime-600 cursor-pointer"
            />
            <label htmlFor="block-vpn-toggle" className="cursor-pointer flex-1">
              <span className="block text-sm font-medium text-slate-900">Also block VPN, proxy &amp; datacentre traffic</span>
              <span className="block text-xs text-slate-500 mt-0.5">Refuses any request from an IP flagged as a known VPN, proxy, Tor exit node, or hosting provider. Uses ip-api.com&apos;s proxy/hosting flags. Legitimate users on a corporate VPN may be affected.</span>
            </label>
          </div>

          {geoblock.updated_at && (
            <p className="text-slate-400 text-[11px] mt-3">Last updated {new Date(geoblock.updated_at).toLocaleString()} {geoblock.updated_by ? `by ${geoblock.updated_by}` : ''}</p>
          )}
          <Button
            onClick={async () => {
              const codes = geoblock.blocked_countries_text
                .split(',')
                .map(c => c.trim().toUpperCase())
                .filter(c => c.length === 2 && /^[A-Z]{2}$/.test(c));
              setSaving(true);
              try {
                await axios.put(
                  `${API}/admin/page-content/geoblock_settings`,
                  { blocked_countries: codes, block_vpn: geoblock.block_vpn },
                  { headers: getAuthHeader() },
                );
                const parts = [];
                if (codes.length) parts.push(`countries: ${codes.join(', ')}`);
                if (geoblock.block_vpn) parts.push('VPN traffic');
                toast.success(parts.length ? `Blocking ${parts.join(' + ')}` : 'Block list cleared');
                setGeoblock({ ...geoblock, blocked_countries_text: codes.join(', ') });
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to save block list');
              }
              setSaving(false);
            }}
            disabled={saving}
            className="mt-3 bg-lime-600 hover:bg-lime-700"
            data-testid="save-geoblock-btn"
          >
            <FloppyDisk size={15} className="mr-1" />Save Block Settings
          </Button>
        </div>
      </div>
    </div>
  );
}
